import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { ClosingCTA } from '../components/ClosingCTA';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { SplitSection, ChecklistSection, FaqSection, RelatedLinks, METRO, BASE_URL, ORG_ID, metroAreaServed } from '../components/LocalPage';

/* 2026-09-17: rewritten in the current house style and routed again at
   /google-business-profile-setup (it had been redirecting to /how-it-works
   since the three-services restructure). Per the 2026-08-13 keyword
   realignment this page is SETUP ONLY. It never says "management" and never
   mentions reviews.
   2026-09-17 (Kevin, ruling): the $397 one-time figure is RETIRED and must not
   come back, and "The Viridia Package is $897 no setup fee." So this page no
   longer offers the profile as a separately quoted one-time job; setup is part
   of onboarding at no setup fee. Do not reintroduce a standalone price here. */

const faqs = [{
  question: 'What is a Google Business Profile?',
  answer: 'The free listing Google shows in Search and Maps when someone looks for a business like yours nearby. It carries your name, phone, hours, services, service area and photos. It is the listing behind the map at the top of a local search, which is where most Phoenix home service calls start.'
}, {
  question: 'Can I set it up myself?',
  answer: 'Yes, and plenty of owners do. The ones who ask us for help usually got stuck on verification, picked the wrong primary category, set a service area Google ignores, or have a profile that is technically live and never shows up. Setup is about getting those details right the first time.'
}, {
  question: 'How long does verification take?',
  answer: 'The profile itself is built in a few days. Verification is on Google. Depending on the method Google asks for (video, phone, or postcard), it can take days or a few weeks. We handle the submission and chase it.'
}, {
  question: 'Will this get me into the map pack?',
  answer: 'It gets you eligible. Map rankings depend on proximity, relevance, and how complete and active the profile is. A correctly set up, verified profile is the foundation. Nothing on this page promises a ranking.'
}, {
  question: 'Is this ongoing management?',
  answer: 'No. This is a one-time setup. Once the profile is built and verified it is yours, in your Google account, with you as the owner. If you want the profile to keep getting calls answered and leads followed up, that is what The Viridia System does.'
}, {
  question: 'What does Google Business Profile setup cost?',
  answer: 'Google does not charge for the profile, and there is no setup fee from us. Building it, setting the categories and service area, and getting it verified is part of onboarding when you join The Viridia System at $897 a month, month to month.'
}];
const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${BASE_URL}/google-business-profile-setup#service`,
  name: 'Google Business Profile Setup',
  serviceType: 'Google Business Profile setup and verification',
  description: 'Google Business Profile setup and verification for Phoenix home service businesses: categories, service area, hours, services, photos and verification, done once and handed back to you. Included with The Viridia System at $897 a month, no setup fee.',
  url: `${BASE_URL}/google-business-profile-setup`,
  provider: {
    '@id': ORG_ID
  },
  areaServed: metroAreaServed
};
export function GbpSetupPage() {
  return <>
      <SEOHead title="Google Business Profile Setup for Phoenix Home Service Businesses | Viridia" description="Google Business Profile setup and verification for Phoenix contractors: the right categories, service area, hours, services and photos, verified and handed back to you. Included with The Viridia System at $897 a month, no setup fee." canonical="/google-business-profile-setup" jsonLd={jsonLd} faq={faqs} data-id="element-2634" data-mp="d7g0kdh9ikdy" />

      <PageHeader label="Local search" title="Google Business Profile setup for Phoenix home service businesses." intro="A one-time Google Business Profile setup: the right categories, a service area Google will honor, hours, services, photos and verification, done once and handed back to you as the owner." data-id="element-2635" data-mp="1fz92lfwzq7aj">
        <div className="flex flex-wrap items-center gap-4" data-id="element-2636" data-mp="sa6atf8n6y2b">
          <CTAButton to="/contact" data-id="element-2637" data-mp="wb9opw74qk0w">Book a 15-min call</CTAButton>
          <CTAButton to="/answering-service-phoenix" variant="ghost" data-id="element-2638" data-mp="11288j5rbu08w">
            Who answers the calls it brings
          </CTAButton>
        </div>
      </PageHeader>

      <SplitSection label="Why it matters" title="Most Phoenix home service calls start on a map." body={<>
            <p data-id="element-2640" data-mp="12y2d8expgno">
              Someone searches for a plumber in Peoria and taps one of the three businesses in the
              map at the top. If your profile is unverified, in the wrong category, or missing a
              service area, you are not one of the three.
            </p>
            <p data-id="element-2641" data-mp="3j818mwgmzypo">The profile is free. Getting it right is the work.</p>
          </>} visual={<DeviceMockup mode="chat" heading="Google Business message" lines={[{
      from: 'customer',
      text: 'Found you on Maps. Do you do drywall repair in Surprise?'
    }, {
      from: 'system',
      text: 'We do. What is the address, and does Wednesday morning work for a look?'
    }]} resultChip="Lead from the map, answered" data-id="element-2642" data-mp="3e8ogbw6zxzfp" />} note="Local search" data-id="element-2639" data-mp="4k3nfcfoaga1" />

      <ChecklistSection alt label="What setup includes" title="Everything that has to be right before Google trusts the profile." columns={3} items={[{
      title: 'Primary and secondary categories',
      body: 'The single biggest lever. The wrong primary category keeps a perfectly good business out of the map.'
    }, {
      title: 'Service area',
      body: 'Set as the cities you actually work in, in a shape Google honors, instead of a radius it ignores.'
    }, {
      title: 'Services and descriptions',
      body: 'Each service you offer listed the way people search for it, with a plain description.'
    }, {
      title: 'Hours, phone and website',
      body: 'Matching exactly what is on your site, your Yelp page and your truck. Mismatches cost trust.'
    }, {
      title: 'Photos',
      body: 'Real work, real crew, real trucks. Google and customers both prefer them to stock images.'
    }, {
      title: 'Verification',
      body: 'Video, phone or postcard, whichever Google asks for. We submit it and chase it until it clears.'
    }]} data-id="element-2643" data-mp="2vhotb0j2azp2" />

      <SplitSection label="What it is not" title="Setup, not management. It is your profile." body={<>
            <p data-id="element-2645" data-mp="16xrubzfpbfxz">
              When it is verified, you own it, in your own Google account. We do not hold the login,
              and there is no monthly fee to keep what was built.
            </p>
            <p data-id="element-2646" data-mp="1gnl5izbxlt1p">
              What the profile brings in still has to be answered. That is the part The Viridia
              System does, if you want it: the calls picked up, the messages replied to, the
              estimates booked.
            </p>
          </>} visual={<div className="rounded-2xl border border-[#1c1c1c] bg-[#0a0a0a] p-7" data-id="element-2647" data-mp="11yovwxu887yz">
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500" data-id="element-2648" data-mp="11wkjtsm6c2nm">Cities we set service areas for</p>
            <ul className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-2649" data-mp="2ddkbcm0p6dgl">
              {METRO.map(city => <li key={city} className="rounded-lg border border-[#1c1c1c] bg-[#0d0d0d] px-3 py-2 text-sm text-neutral-300" data-id="element-2650" data-mp="29cs2htvqmaku">
                  {city}
                </li>)}
            </ul>
          </div>} note="One-time setup" data-id="element-2644" data-mp="3ndekprk66bmo" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-2651" data-mp="bmboz85dtca1">
        <div className="mx-auto w-full max-w-3xl px-6 py-20 text-center lg:py-24" data-id="element-2652" data-mp="glwobjogrmna">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-2653" data-mp="lgkfxrerkx63">
            Included with The Viridia System. No setup fee.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-2654" data-mp="udupn8bxoe8w">
            Google does not charge for the Business Profile, and neither do we. Building it,
            setting the categories and the service area, and getting it verified is part of
            onboarding when you join The Viridia System at $897 a month. Month to month, no
            contract, no setup fee.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4" data-id="element-2655" data-mp="364ys67xrdh99">
            <CTAButton to="/contact" data-id="element-2656" data-mp="1nadr7rgww1tt">Book a 15-min call</CTAButton>
            <CTAButton to="/pricing#viridia-system" variant="ghost" data-id="element-2657" data-mp="1iic5zg1rn0rn">
              See The Viridia System
            </CTAButton>
          </div>
        </div>
      </section>

      <FaqSection title="Questions about Google Business Profile setup." faqs={faqs} data-id="element-2658" data-mp="19i5xk8kxcqzc" />

      <RelatedLinks title="Related pages" links={[{
      to: '/answering-service-phoenix',
      label: 'Answering service in Phoenix',
      body: 'Who picks up when the profile starts sending calls.'
    }, {
      to: '/websites',
      label: 'Websites',
      body: 'The site the profile links to, built and hosted by Viridia.'
    }, {
      to: '/yelp-ads',
      label: 'Yelp Ads Management',
      body: 'The other local channel, run by a former Yelp rep.'
    }]} data-id="element-2659" data-mp="19g30x5p9nzln" />

      <ClosingCTA data-id="element-2660" data-mp="lu66f7jpayk8" />
    </>;
}